import CIHotspot from "@cloudimage/hotspot"

const ENTER_FULLSCREEN_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" x2="14" y1="3" y2="10"/><line x1="3" x2="10" y1="21" y2="14"/></svg>'

const EXIT_FULLSCREEN_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" x2="21" y1="10" y2="3"/><line x1="3" x2="10" y1="21" y2="14"/></svg>'

export function isNativeFullscreenSupported() {
  return !!(document.fullscreenEnabled || document.webkitFullscreenEnabled)
}

export function attachPseudoFullscreen(viewer) {
  if (isNativeFullscreenSupported()) return null

  const container = viewer.getElements?.()?.container
  if (!container) return null

  const button = document.createElement("button")
  button.type = "button"
  button.className = "ci-hotspot-fullscreen-btn"
  button.setAttribute("aria-label", "Enter fullscreen")
  button.setAttribute("aria-pressed", "false")
  button.innerHTML = ENTER_FULLSCREEN_ICON

  let active = false
  let scrollY = 0
  let overlay = null
  let originalParent = null
  let originalNextSibling = null

  const updateButton = () => {
    button.innerHTML = active ? EXIT_FULLSCREEN_ICON : ENTER_FULLSCREEN_ICON
    button.setAttribute("aria-label", active ? "Exit fullscreen" : "Enter fullscreen")
    button.setAttribute("aria-pressed", String(active))
  }

  const restoreContainer = () => {
    if (!originalParent) return

    if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
      originalParent.insertBefore(container, originalNextSibling)
    } else {
      originalParent.appendChild(container)
    }

    originalParent = null
    originalNextSibling = null
  }

  const enter = () => {
    if (active) return

    active = true
    scrollY = window.scrollY
    originalParent = container.parentNode
    originalNextSibling = container.nextSibling

    overlay = document.createElement("div")
    overlay.className = "hotspot-pseudo-fullscreen-overlay"
    overlay.setAttribute("role", "dialog")
    overlay.setAttribute("aria-modal", "true")
    overlay.setAttribute("aria-label", "Device image fullscreen view")
    overlay.appendChild(container)
    document.body.appendChild(overlay)

    container.classList.add("ci-hotspot-container--fullscreen", "ci-hotspot-container--pseudo-fullscreen")
    document.body.classList.add("hotspot-pseudo-fullscreen-open")
    document.body.style.top = `-${scrollY}px`
    updateButton()
  }

  const exit = () => {
    if (!active) return

    active = false
    container.classList.remove("ci-hotspot-container--fullscreen", "ci-hotspot-container--pseudo-fullscreen")
    restoreContainer()
    overlay?.remove()
    overlay = null

    document.body.classList.remove("hotspot-pseudo-fullscreen-open")
    document.body.style.top = ""
    window.scrollTo(0, scrollY)
    updateButton()
  }

  const toggle = () => {
    if (active) exit()
    else enter()
  }

  const onKeydown = (event) => {
    if (event.key === "Escape" && active) {
      event.preventDefault()
      exit()
    }
  }

  button.addEventListener("click", toggle)
  document.addEventListener("keydown", onKeydown)
  container.appendChild(button)

  return () => {
    exit()
    button.remove()
    document.removeEventListener("keydown", onKeydown)
  }
}

export function createCIHotspotViewer(element, config) {
  const viewer = new CIHotspot(element, config)
  const cleanupPseudoFullscreen = attachPseudoFullscreen(viewer)

  return { viewer, cleanupPseudoFullscreen }
}
